import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Dices, TrendingDown, TrendingUp, Clock3 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { walletApi } from "../lib/api";
import { inr, timeFrom } from "../lib/format";

const gameOf = (t) => (t.note || "").split(" \u00b7 ")[0] || "Game";
const kindOf = (t) => {
  const tail = ((t.note || "").split(" \u00b7 ")[1] || "").toLowerCase();
  if (tail.startsWith("cashout")) return { label: `Cashout ${tail.replace("cashout ", "")}`, win: true };
  if (tail === "bet") return { label: "Bet placed", win: false };
  return t.amount >= 0 ? { label: "Win", win: true } : { label: "Loss", win: false };
};
const dayKey = (iso) => new Date(iso).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });

export default function GameHistory() {
  const { isAuthed } = useApp();
  const [data, setData] = useState(null);
  const [game, setGame] = useState("All");

  useEffect(() => {
    if (!isAuthed) return;
    walletApi.gameHistory().then(setData).catch(() => setData({ days: 15, items: [] }));
  }, [isAuthed]);

  const items = data?.items || [];
  const games = useMemo(() => ["All", ...Array.from(new Set(items.map(gameOf)))], [items]);
  const rows = game === "All" ? items : items.filter((t) => gameOf(t) === game);
  const net = rows.reduce((s, t) => s + Number(t.amount || 0), 0);

  const grouped = useMemo(() => {
    const m = new Map();
    rows.forEach((t) => { const k = dayKey(t.at); if (!m.has(k)) m.set(k, []); m.get(k).push(t); });
    return Array.from(m.entries());
  }, [rows]);

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-[#0b0b0b] px-4 py-16 text-center text-muted-foreground" data-testid="game-history-signin">
        Sign in to view your game history.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0b0b] pb-28" data-testid="game-history-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
        <div className="flex items-center gap-3 mb-5">
          <Link to="/wallet" className="grid place-items-center h-9 w-9 rounded-full border border-white/10 bg-white/5 text-white hover:bg-white/10 transition-colors" data-testid="game-history-back" aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <h1 className="font-display font-extrabold text-2xl text-white leading-tight">Game History</h1>
            <p className="text-xs text-zinc-400 flex items-center gap-1.5"><Clock3 className="h-3 w-3" /> Last {data?.days ?? 15} days · older rounds are removed automatically</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-transparent p-4">
            <div className="text-[11px] uppercase tracking-wider text-zinc-400">Rounds</div>
            <div className="font-display font-extrabold text-2xl text-white tabular-nums" data-testid="game-history-rounds">{rows.length}</div>
          </div>
          <div className={`rounded-2xl border p-4 ${net >= 0 ? "border-emerald-500/30 bg-emerald-500/10" : "border-red-500/30 bg-red-500/10"}`}>
            <div className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1">
              {net >= 0 ? <TrendingUp className="h-3 w-3 text-emerald-400" /> : <TrendingDown className="h-3 w-3 text-red-400" />} Net result
            </div>
            <div className={`font-display font-extrabold text-2xl tabular-nums ${net >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="game-history-net">
              {net >= 0 ? "+" : "−"}{inr(Math.abs(net))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="game-history-filters">
          {games.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGame(g)}
              data-testid={`game-history-filter-${g.toLowerCase().replace(/\s+/g, "-")}`}
              className={`shrink-0 h-8 px-3.5 rounded-full text-xs font-bold border transition-[color,background-color,border-color] ${
                game === g ? "bg-[#f5b400] text-black border-[#f5b400]" : "bg-white/5 text-zinc-300 border-white/10 hover:border-white/30"
              }`}
            >
              {g}
            </button>
          ))}
        </div>

        {!data && <div className="py-16 text-center text-zinc-500">Loading…</div>}
        {data && rows.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] py-14 text-center" data-testid="game-history-empty">
            <Dices className="h-8 w-8 mx-auto text-zinc-600 mb-2" />
            <div className="text-zinc-400 text-sm">No game rounds in the last {data.days} days.</div>
            <Link to="/" className="inline-block mt-4 rounded-full bg-[#f5b400] text-black text-sm font-extrabold px-5 py-2">Play now</Link>
          </div>
        )}

        {grouped.map(([day, list]) => (
          <section key={day} className="mb-5" data-testid="game-history-day">
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 px-1 mb-2">{day}</div>
            <div className="rounded-2xl border border-white/10 bg-[#121212] divide-y divide-white/5 overflow-hidden">
              {list.map((t) => {
                const k = kindOf(t);
                return (
                  <div key={t.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`game-history-row-${t.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`grid place-items-center h-9 w-9 rounded-xl shrink-0 ${k.win ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                        <Dices className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-white truncate">{gameOf(t)}</div>
                        <div className="text-xs text-zinc-400 truncate">{k.label} · {timeFrom(t.at)}</div>
                      </div>
                    </div>
                    <div className={`font-display font-bold tabular-nums shrink-0 ${t.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {t.amount >= 0 ? "+" : "−"}{inr(Math.abs(t.amount))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
